import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ReportsEmployeeItem } from './reportsEmployeeItem';
import AppIntersectionObserver from '../app/appIntersectionObserver';
import AppLoading from '../app/appLoading';
import { useEmployeesSummary } from '../../hooks/reports/useEmployeesSummary';
import { AppNoFeedback } from '../app/appNoFeedback';
import {
  Box,
  Dialog,
  IconButton,
  Tab,
  Tabs,
  Theme,
  useTheme,
} from '@mui/material';
import { ColorPalette, TypographyFamily } from '../../styles/constants';
import { useReportsFilters } from '../../context/reports/useReportsCtx';
import { DEFAULT_PAGE_SIZE } from '../../constants';
import { ROLES_ID } from '../../models/employee';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { AppDialogTitle } from '../../layout/appDialogTitle';
import { EmployeeOption, ReportsTeamLeadSelect } from './reportsTeamLeadSelect';
import { EmployeesSummary } from '../../models/reports';
import { setEmployeeCardState } from '../../store/reports/reportsSlice';
import { useAppDispatch } from '../../hooks';
import { ActivityFormData } from '../activity/activityForm';

interface Props {
  viewUnloggedEmployees: boolean;
  viewShowInactiveEmployees: boolean;
}

const makeStyles = (theme: Theme) => ({
  topBox: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },

  allocationsRow: {
    mb: 2,
  },
  allocationsBtn: {
    textDecorationColor: ColorPalette.GREEN,
    fontFamily: TypographyFamily.NEXA,
  },
  addTabBtn: {
    color: ColorPalette.DARK_GRAY,
  },
  backBtnContent: { display: 'flex', alignItems: 'center', fontSize: 16 },
  mobileItemsRow: { display: 'flex', flexDirection: 'column' },
  mobileViewsBox: { pt: 2, pr: 1, pl: 1 },
  backIcon: {
    mr: 2,
    fill: `url(#my-cool-gradient) ${theme.palette.primary.main}`,
  },
  tabContainer: {
    minHeight: '36px',
  },
  tabEdit: {
    '&.Mui-selected': {
      backgroundColor: '#80C1C3',
      borderRadius: '8px 8px 0 0',
      color: '#332E38',
    },
    '&.MuiTab-textColorPrimary': {
      color: '#332E38',
      textTransform: 'none',
      minHeight: '36px',
    },
  },
});

export function ReportsEmployeeList({
  viewUnloggedEmployees,
  viewShowInactiveEmployees,
}: Props): JSX.Element {
  const theme = useTheme();
  const styles = makeStyles(theme);

  const [skip, setSkip] = useState(0);
  const [tabs, setTabs] = useState<EmployeeOption[]>([]);
  const [selectedTL, setSelectedTL] = useState<EmployeeOption>();
  const [teamLeads, setTeamLeads] = useState<EmployeesSummary[]>([]);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [isSelectTeamLead, setIsSelectTeamLead] = useState(false);

  const filters = useReportsFilters();

  const {
    items,
    isLoading,
    isFetching,
    fetchData,
    isEmpty,
    hasMore,
    fetchEmployeeData,
  } = useEmployeesSummary();

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (filters) {
      if (filters.managerId && filters.managerName) {
        const initialPayload = {
          label: filters.managerName,
          id: filters.managerId,
        };
        setSelectedTL(initialPayload);
        setTabs([{ id: filters.managerId, label: 'Direct Reports' }]);
        setActiveTabIndex(0);
      }

      fetchData(filters, viewUnloggedEmployees, viewShowInactiveEmployees);
      setSkip(0);
    }
  }, [filters, fetchData, viewUnloggedEmployees, viewShowInactiveEmployees]);

  const initialTL = useMemo<EmployeeOption | null>(
    () =>
      filters && filters.managerId && filters.managerName
        ? { label: filters.managerName, id: filters.managerId }
        : null,
    [filters]
  );

  const loadEmployeeTab = useCallback(
    (employee: EmployeeOption) => {
      if (!filters || !employee) {
        return;
      }
      setSelectedTL(employee);
      fetchData(
        {
          ...filters,
          managerId: employee.id,
          managerName: employee.label,
          skip: 0,
        },
        viewUnloggedEmployees,
        viewShowInactiveEmployees
      );
      setSkip(0);
    },
    [filters, fetchData, viewUnloggedEmployees, viewShowInactiveEmployees]
  );

  const addEmployeeTab = useCallback(
    (employee: EmployeeOption) => {
      const updatedTabs = [...tabs, { label: employee.label, id: employee.id }];
      setTabs(updatedTabs);
      setActiveTabIndex(updatedTabs.length - 1);

      loadEmployeeTab(employee);
    },
    [tabs, loadEmployeeTab]
  );

  const handleObserver = useCallback(() => {
    if (isLoading || isFetching || !hasMore || !filters) {
      return;
    }

    fetchData(
      {
        ...filters,
        managerId: selectedTL ? selectedTL.id : filters.managerId,
        managerName: selectedTL ? selectedTL.label : filters.managerName,
        skip: skip + DEFAULT_PAGE_SIZE,
      },
      viewUnloggedEmployees,
      viewShowInactiveEmployees
    );
    setSkip(skip + DEFAULT_PAGE_SIZE);
  }, [
    isLoading,
    isFetching,
    hasMore,
    filters,
    fetchData,
    selectedTL,
    skip,
    viewUnloggedEmployees,
    viewShowInactiveEmployees,
  ]);

  const handleChangeTab = useCallback(
    (event, newTabIndex: number) => {
      setActiveTabIndex(newTabIndex);
      if (newTabIndex === 0 && initialTL) {
        loadEmployeeTab(initialTL);
      } else {
        loadEmployeeTab(tabs[newTabIndex]);
      }
    },
    [loadEmployeeTab, tabs, initialTL]
  );

  useEffect(() => {
    if (activeTabIndex === 0) {
      const leads = items.filter((item) =>
        item.employee.permissionGroups.some(
          (group) => group.id === ROLES_ID.TEAM_LEAD
        )
      );
      setTeamLeads(leads);
    }
  }, [items, activeTabIndex]);

  const handleOpenSelectTeamLead = useCallback(() => {
    setIsSelectTeamLead((prev) => !prev);
  }, []);

  const userTeamleadFiltersExists = useMemo(() => {
    if (!filters?.employeeId) {
      return true;
    }
    const userExists = teamLeads.some(
      (employeeObj) => employeeObj.employee.id === filters?.employeeId
    );

    return userExists;
  }, [filters, teamLeads]);

  const isTabsVisible = useMemo(
    () =>
      filters &&
      teamLeads &&
      teamLeads.length > 0 &&
      filters.managerId &&
      filters.managerName &&
      userTeamleadFiltersExists,
    [teamLeads, filters, userTeamleadFiltersExists]
  );

  const handleCloseTab = useCallback(
    (indexToRemove, event) => {
      event.stopPropagation();

      const tabsUpdated = [...tabs];
      tabsUpdated.splice(indexToRemove, 1);
      setTabs(tabsUpdated);

      if (indexToRemove === activeTabIndex) {
        const currentTabIndex = indexToRemove - 1;
        setActiveTabIndex(currentTabIndex);

        if (currentTabIndex === 0 && initialTL) {
          loadEmployeeTab(initialTL);
        } else {
          loadEmployeeTab(tabsUpdated[currentTabIndex]);
        }
      }

      if (indexToRemove < activeTabIndex) {
        setActiveTabIndex((prev) => prev - 1);
      }
    },
    [tabs, loadEmployeeTab, activeTabIndex, initialTL]
  );
  const reloadEmployeeData = useCallback(
    (
      employeeId: string,
      employeeName: string,
      lastLoggedActivity: ActivityFormData | null
    ) => {
      if (!filters) return;

      dispatch(setEmployeeCardState({ employeeId, lastLoggedActivity }));
      fetchEmployeeData(
        { ...filters, employeeId, employeeName },
        viewUnloggedEmployees,
        viewShowInactiveEmployees
      );
    },
    [
      filters,
      dispatch,
      fetchEmployeeData,
      viewUnloggedEmployees,
      viewShowInactiveEmployees,
    ]
  );

  return (
    <Box sx={styles.topBox}>
      {isTabsVisible && (
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Tabs
            value={activeTabIndex}
            onChange={handleChangeTab}
            sx={styles.tabContainer}
          >
            {tabs.map((tab, index) => (
              <Tab
                key={tab.label}
                label={tab.label}
                sx={styles.tabEdit}
                icon={
                  index !== 0 ? (
                    <Box
                      onClick={(event) => handleCloseTab(index, event)}
                      style={{
                        cursor: 'pointer',
                        marginTop: '4px',
                        marginLeft: '8px',
                      }}
                    >
                      <CloseIcon style={{ fontSize: 12 }} color="action" />
                    </Box>
                  ) : undefined
                }
                iconPosition="end"
              />
            ))}
          </Tabs>
          {isTabsVisible && (
            <IconButton
              sx={styles.addTabBtn}
              style={{ marginLeft: 4 }}
              onClick={handleOpenSelectTeamLead}
            >
              <AddIcon style={{ fontSize: 20 }} />
            </IconButton>
          )}
        </Box>
      )}

      {!isEmpty &&
        !isLoading &&
        filters &&
        items.map((item) => {
          return (
            <ReportsEmployeeItem
              data={item}
              reloadEmployeeData={reloadEmployeeData}
              startDateFilter={filters.startDate}
              endDateFilter={filters.endDate}
              projectIdFilter={filters.projectId}
              key={item.employee.id}
              handleAddTeamLeadTab={addEmployeeTab}
              showTeamLeadIcon={isTabsVisible}
            />
          );
        })}

      <AppIntersectionObserver intersect={handleObserver} />

      {(isLoading || isFetching) && <AppLoading />}
      {isEmpty && <AppNoFeedback info="data" />}

      <Dialog open={isSelectTeamLead} sx={{ borderRadius: '5px' }}>
        <AppDialogTitle
          title="Pick a Team Lead"
          handleClose={handleOpenSelectTeamLead}
        />
        <ReportsTeamLeadSelect
          handleClose={handleOpenSelectTeamLead}
          managerId={initialTL?.id}
          handleDisplay={addEmployeeTab}
        />
      </Dialog>
    </Box>
  );
}
